import type { CreativeAd, ObjectId, TagCount, TagRef } from '../types'
import { relativePct } from './relativeMetric'

// Tag dimensions never surfaced in the UA Insights UI. The API is expected to
// strip these server-side; this local set keeps the compare view defensive.
const HIDDEN_TAG_DIMENSIONS: ReadonlySet<string> = new Set(['text_overlays'])

/**
 * Minimum number of carrier ads a tag must have in a game before it qualifies
 * for inclusion in the per-group breakdown / crown-jewel ranking. Set to `1`
 * so that low-volume advertisers (e.g. games running a single ad in the
 * selected network/month) still surface their tags; the trade-off is that a
 * single hit ad can drive a tag to the top of its group's ranking, so chip
 * values for thin scopes should be read as "this ad's tags" more than "this
 * game's broader pattern". Raise back to `2` if long-tail noise becomes a
 * problem on real data.
 */
const MIN_CARRIER_FLOOR = 1

/**
 * Headliner tag groups always rendered (and expanded) in the comparison view.
 * Order is meaningful: it doubles as the rendering order, descending in
 * "story-shaping" power for UA managers (narrative comes first because it
 * carries the most signal about replicable creative patterns).
 */
export const HEADLINER_GROUPS: readonly string[] = [
  'narrative_function',
  'value_proposition',
  'emotional_tone',
  'setting',
  'subjects',
  'action',
  'game_genre_signals',
  'content_type',
  'visual_style',
  'audio_cues',
]

/**
 * Execution-detail tag groups rendered behind a "show more groups" disclosure
 * — these describe how an ad is shot rather than what story it tells, so they
 * are secondary to the headliner groups for creative-pattern analysis.
 *
 * Groups present in {@link HIDDEN_TAG_DIMENSIONS} are filtered out at module
 * load so we can keep the canonical list of every group the data layer knows
 * about here while still respecting the global hide list (e.g. the raw-OCR
 * `text_overlays` group is omitted from the UI even though the data ships it).
 */
export const EXECUTION_DETAIL_GROUPS: readonly string[] = (
  [
    'color_palette',
    'reward_visualization',
    'progress_visualization',
    'pacing',
    'shot_type',
    'camera_movement',
    'player_perspective',
    'comparison_framing',
    'failure_shown',
    'social_element',
    'finger_interaction',
    'objects',
    'ui_elements',
    'text_type',
    'text_overlays',
    'muted_comprehension',
  ] as const
).filter((group) => !HIDDEN_TAG_DIMENSIONS.has(group))

/**
 * Combined, ordered list of every tag group surfaced in the comparison view.
 * Headliners first (rendered expanded), execution-detail second (rendered
 * collapsed). Groups not present in either game's data are skipped at render
 * time, so this constant only needs to enumerate every group we *might* show.
 */
export const TAG_GROUP_ORDER: readonly string[] = [...HEADLINER_GROUPS, ...EXECUTION_DETAIL_GROUPS]

const HEADLINER_GROUP_SET = new Set(HEADLINER_GROUPS)

/** Whether a tag-group key is rendered as a headliner (expanded by default). */
export const isHeadlinerGroup = (group: string): boolean => HEADLINER_GROUP_SET.has(group)

/** Per-game stats for a single tag, computed against that game's creative-ad set. */
export interface TagStats {
  /**
   * Mean **Ad Performance** across the game's creatives that carry the tag
   * (0-100). Per-ad performance follows the formula `CreativeAdsTable` uses
   * for its `col_ad_performance` column: `(ad.installShare / scopeMaxInstallShare) * 100`,
   * where `scopeMaxInstallShare` is the strongest individual creative's install
   * share across **every game on the selected network/month**. The scope-wide
   * anchor keeps a tag's score independent of which two games happen to be in
   * the compare view. Drives the sort order within each tag group.
   */
  perf: number
  /**
   * Number of the game's creatives that carry this tag in the current
   * (network/month) scope. Surfaced as a confidence indicator alongside
   * `perf` and used to apply the {@link MIN_CARRIER_FLOOR} cutoff.
   */
  carrierCount: number
  /**
   * Share-of-ads percentage (0-100): `carrierCount / totalCreatives * 100`.
   * Secondary signal that captures "how heavily the game leans on this tag",
   * complementary to `perf` (which is "how well ads with this tag perform").
   */
  share: number
}

/**
 * One row of the cross-game comparison: a single tag with each game's stats
 * (or `null` when the tag is absent from / below the floor in that game).
 * The bucketing logic in {@link bucketGroupRows} reads `src` / `tgt` to decide
 * whether the row is shared, source-only, or target-only.
 */
export interface GroupedTagComparison {
  tag: TagRef
  /** Dimension `code` of the tag, the key of the group it renders in. */
  dimension: string
  src: TagStats | null
  tgt: TagStats | null
}

/** Result of bucketing a tag group's rows for the 3-column display. */
export interface BucketedGroupRows {
  shared: GroupedTagComparison[]
  sourceOnly: GroupedTagComparison[]
  targetOnly: GroupedTagComparison[]
}

/** The two parts of a game (or genre aggregate) payload the comparison reads. */
export interface TagComparisonSide {
  tags: TagCount[]
  creativeAds: CreativeAd[]
}

/**
 * Computes per-tag stats for the given game's creatives. Returns `null` for
 * tags that don't appear at all in this game's creatives (caller can then
 * decide whether to keep the row anyway with `null` stats — e.g. when the
 * other game uses the tag — or drop it entirely).
 *
 * Each carrier ad's Ad Performance is `(ad.installShare / scopeMaxInstallShare) * 100`,
 * matching the `CreativeAdsTable.col_ad_performance` formula, and the tag's
 * `perf` is the mean of its carriers' Ad Performance values.
 *
 * Stats are *not* floor-filtered here; that filter is applied later by
 * {@link bucketGroupRows} and the consumers, so the same per-tag computation
 * can serve different presentation rules without recomputing.
 */
function computeTagPerf(creatives: CreativeAd[], tagId: ObjectId, scopeMaxInstallShare: number): TagStats | null {
  const totalCreatives = creatives.length
  if (totalCreatives === 0) return null

  let carrierCount = 0
  let perfSum = 0
  for (const ad of creatives) {
    if (ad.tags.some((tag) => tag.id === tagId)) {
      carrierCount += 1
      // Per-ad Ad Performance, identical to the score `CreativeAdsTable`
      // shows in its `col_ad_performance` column — which is what
      // `relativePct` computes, so sharing it keeps the two in step rather
      // than restating the formula. It also supplies the 0-100 clamp and the
      // divide-by-zero guard for a comparison that contains no ads.
      perfSum += relativePct(ad.installShare ?? 0, scopeMaxInstallShare)
    }
  }
  if (carrierCount === 0) return null

  return {
    perf: perfSum / carrierCount,
    carrierCount,
    share: (carrierCount / totalCreatives) * 100,
  }
}

/**
 * Builds the union of tags across both sides, indexed by their dimension
 * `code` (a.k.a. tag group). Each tag carries per-side stats; tags missing from
 * one side contribute a `null` stats entry on that side. `dimensionCode`
 * resolves a tag's `dimensionId` through `/meta`.
 */
export function groupTagsByDimension(
  source: TagComparisonSide,
  target: TagComparisonSide,
  scopeMaxInstallShare: number,
  dimensionCode: (dimensionId: ObjectId) => string | undefined
): Map<string, GroupedTagComparison[]> {
  const rowByTag = new Map<ObjectId, GroupedTagComparison>()

  const upsert = (tag: TagRef): GroupedTagComparison => {
    const existing = rowByTag.get(tag.id)
    if (existing) return existing
    const next: GroupedTagComparison = { tag, dimension: dimensionCode(tag.dimensionId) ?? '', src: null, tgt: null }
    rowByTag.set(tag.id, next)
    return next
  }

  for (const { tag } of source.tags) {
    upsert(tag).src = computeTagPerf(source.creativeAds, tag.id, scopeMaxInstallShare)
  }
  for (const { tag } of target.tags) {
    upsert(tag).tgt = computeTagPerf(target.creativeAds, tag.id, scopeMaxInstallShare)
  }

  const byDimension = new Map<string, GroupedTagComparison[]>()
  for (const row of rowByTag.values()) {
    // Drop rows with no dimension and any hidden dimension here — this is the
    // single chokepoint every consumer reads from (including the "other
    // dimensions" fallbacks), so excluding hidden groups once keeps them out
    // of both the canonical groups and the unknown-group catch-all.
    if (!row.dimension || HIDDEN_TAG_DIMENSIONS.has(row.dimension)) continue
    const list = byDimension.get(row.dimension) ?? []
    list.push(row)
    byDimension.set(row.dimension, list)
  }
  return byDimension
}

/**
 * Whether a per-game tag-stats entry meets the carrier floor. Tags below the
 * floor are treated as "not used" by that game for bucketing/ranking purposes
 * — the underlying carrier count is still kept on the row in case a consumer
 * wants to render it with a muted treatment instead of dropping it.
 */
const meetsFloor = (stats: TagStats | null): boolean => stats !== null && stats.carrierCount >= MIN_CARRIER_FLOOR

const byName = (a: GroupedTagComparison, b: GroupedTagComparison): number => a.tag.name.localeCompare(b.tag.name)

/**
 * Splits a tag group's rows into the three columns of the diagnosis layout:
 *
 *  - `shared`     — both games meet the carrier floor; ranked by combined
 *                   `perf` desc (strongest cross-game signature first).
 *  - `sourceOnly` — only source meets the floor; ranked by source `perf` desc.
 *  - `targetOnly` — only target meets the floor; ranked by target `perf` desc.
 *
 * Rows where neither game meets the floor are dropped entirely. The output is
 * ready for direct rendering by the 3-column diagnosis primitives.
 */
export function bucketGroupRows(rows: GroupedTagComparison[]): BucketedGroupRows {
  const shared: GroupedTagComparison[] = []
  const sourceOnly: GroupedTagComparison[] = []
  const targetOnly: GroupedTagComparison[] = []

  for (const row of rows) {
    const srcOk = meetsFloor(row.src)
    const tgtOk = meetsFloor(row.tgt)
    if (srcOk && tgtOk) shared.push(row)
    else if (srcOk) sourceOnly.push(row)
    else if (tgtOk) targetOnly.push(row)
  }

  shared.sort((a, b) => {
    const aSum = (a.src?.perf ?? 0) + (a.tgt?.perf ?? 0)
    const bSum = (b.src?.perf ?? 0) + (b.tgt?.perf ?? 0)
    return bSum - aSum || byName(a, b)
  })
  sourceOnly.sort((a, b) => (b.src?.perf ?? 0) - (a.src?.perf ?? 0) || byName(a, b))
  targetOnly.sort((a, b) => (b.tgt?.perf ?? 0) - (a.tgt?.perf ?? 0) || byName(a, b))

  return { shared, sourceOnly, targetOnly }
}

/**
 * Per-game ranking helper for the crown-jewel summary. Returns the game's
 * tags inside a single group ordered by `perf` desc, restricted to those
 * meeting the carrier floor.
 */
export function rankTagsForGame(rows: GroupedTagComparison[], side: 'src' | 'tgt'): GroupedTagComparison[] {
  return rows
    .filter((row) => meetsFloor(row[side]))
    .sort((a, b) => {
      const aPerf = a[side]?.perf ?? 0
      const bPerf = b[side]?.perf ?? 0
      return bPerf - aPerf || byName(a, b)
    })
}
